// ============================================================================
// VigiaCrm · o vigia de mensagem nova do CRM em todas as telas do DNA (30/10/2026)
// ============================================================================
// Pedido do dono (30/10/2026): "Preciso fazer notificação de mensagem para quando a
// recepcionista estiver no computador e estiver fora do DNA, por exemplo no Avec, ou
// então para quando ela estiver em outra aba do DNA e não tiver no CRM. Como não está
// chegando nenhuma notificação nem nada, elas estão se perdendo. O som também não está
// funcionando."
// Montado no Layout (CRM_VIGIA) para quem tem o "CRM da Sofia" no menu (o mesmo portão
// do Sidebar) e SÓ NO COMPUTADOR (eComputador do contrato; iPad com Safari conta): no
// celular nada muda, a Caixa segue tocando dentro do CRM. As regras moram em
// src/utils/crmVigia.js; aqui só a ligação com a tela:
//   · uma aba líder por navegador (navigator.locks; sem ele, a batida no localStorage):
//     só ela lê o GET ?op=avisos (leve: só ids, unidade, hora e quantas; com o carimbo da
//     leitura anterior, o servidor responde mudou: false sem ler as falas) e avisa; as
//     outras abas só põem o "(N)" no título, pelo evento storage;
//   · a líder lê no aviso do canal ao vivo 'crm-caixa' (crmTempoReal.js com a aba
//     sempre "visível" para ele: o canal não fecha com o Avec na frente; cliente próprio
//     com o batimento num Web Worker, que o Chrome não segura com a aba escondida),
//     no máximo 1 leitura a cada 30 s de dia e 60 s à noite, nunca antecipada com a Caixa
//     à vista, e na reserva (60 s, 180 s com o canal provado, 5 min das 21h às 8h);
//   · recusa passageira não desliga: só 404 (rollout off) ou a falta do token param de
//     vez; 401 e 403 esperam 5 min e 3 seguidas param até a sessão mudar (o Vigia remonta
//     pela pessoa e pelo token: key do componente);
//   · UM SOM SÓ, SEMPRE (crmVigia.criarAvisador): com o push do computador ativo, o vigia
//     não toca nada por conta própria (o service worker manda MSG_PUSH_CHEGOU a todas as
//     janelas do DNA e pede o som à em foco; a fala que a chegada cobre só é marcada; a
//     que não, espera ESPERA_PUSH_MS e confere de novo); sem push, a Caixa à vista toca,
//     senão a líder com foco, senão a aba em foco a pedido (CHAVE_VIGIA_TOCAR), senão o
//     aviso do navegador (nunca com o push daquela conversa na tela), senão o som na líder;
//   · o leitor do service worker (todas as abas): o clique no push abre a conversa pelo
//     roteador e responde { ok: true }; a chegada do push grava e marca e, com pedirSom,
//     toca se esta aba tem foco e responde { tocou }; a troca da inscrição refaz a do
//     computador (reinscreverSeConcedido com forcar);
//   · o som destrava em qualquer clique ou tecla do DNA; travado (depois de uma recarga,
//     aqui ou na Caixa pelo EVENTO_SOM_TRAVADO), aparece no canto "Clique aqui para ligar
//     o som do CRM" (o clique liga e toca uma vez);
//   · reinscreverSeConcedido(user) uma vez ao montar (em silêncio, nunca pede permissão).
// Nada de dado do cliente: o aviso do navegador diz só a unidade (crmAvisoContrato.js).
// CRM_VIGIA = false: o Layout nem monta este componente.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Volume2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useData } from '../../contexts/DataContext.jsx';
import { useAtendimentoAcesso } from '../../utils/useAtendimentoAcesso.js';
import { CRM_VIGIA, CRM_SOFIA_MENU, IA_ATENDENTE_MENU, CRM_VISUAL_V2, CRM_VISUAL_V3, CRM_SOM, CRM_APARENCIA } from '../../config/featureFlags.js';
import {
  criarLideranca, criarAgenda, criarAvisador, criarLeitorDoServiceWorker, processarLeitura, depoisDaFalha, marcarVigiaMontado,
  pedirSomNaAbaComFoco, responderPedidoDeSom, lerTotalDasAbas, tituloComContagem, tirarPrefixo,
  naRotaDoCrm, usaFilaNoMenu, vigiaLigadoPara,
} from '../../utils/crmVigia.js';
import { caixaAtivaAgora } from '../../utils/crmCaixaAtiva.js';
import { prenderDestravar, tocarAviso, testarSom, ouvirSom } from '../../utils/crmSom.js';
import { lerPrefs, chavePrefs, validarPrefs } from '../../utils/crmPreferencias.js';
import { criarTempoReal, clientePadrao } from '../../utils/crmTempoReal.js';
import { sessionToken } from '../../utils/secureRest.js';
import {
  CHAVE_VIGIA_TOTAL, CHAVE_VIGIA_TOCAR, ESPERA_PUSH_MS, EVENTO_SOM_TRAVADO, TITULO_AVISO_MENSAGEM,
  corpoAvisoMensagem, tagAvisoMensagem, urlAvisoMensagem, eComputador,
} from '../../utils/crmAvisoContrato.js';
import { avisosComputadorAtivos, reinscreverSeConcedido } from '../../utils/crmAvisoComputador.js';
import { NOME_CURTO } from '../../utils/atendimentoApi.js';

// as chaves e as esperas do contrato, com o mesmo nome de antes (quem importava daqui segue funcionando)
export { CHAVE_VIGIA_TOCAR, ESPERA_PUSH_MS };

// os mesmos interruptores do comum.jsx do CRM (sem trazer as peças da tela para todas as páginas)
const V3_LIGADO = CRM_VISUAL_V2 === true && CRM_VISUAL_V3 === true;
const SOM = V3_LIGADO && CRM_SOM === true;
const APARENCIA = V3_LIGADO && CRM_APARENCIA === true;
export const TEXTO_LIGAR_SOM = 'Clique aqui para ligar o som do CRM';

// ── o cliente do Realtime do vigia: um só dele (dois canais 'crm-caixa' no mesmo cliente se atropelam
// quando a Caixa também está aberta nesta aba), com o batimento num Web Worker (a aba escondida do Chrome
// atrasa os relógios da página e o canal caía). A URL e a chave são as do cliente da Caixa
// (crmTempoReal.clientePadrao: públicas por desenho, só abrem o canal público de avisos, sem dado, e o
// que o RLS deixa); a chave de sessão própria evita o aviso de "dois clientes". Só baixado na aba líder.
let clientePromessa = null;
let aoBatimento = null;
const batimento = (status) => { try { aoBatimento?.(status); } catch { /* segue */ } };
export function clienteDoVigia({ base = clientePadrao, pacote = () => import('@supabase/supabase-js'), comWorker = typeof window !== 'undefined' && typeof window.Worker === 'function' } = {}) {
  if (!clientePromessa) {
    clientePromessa = Promise.all([base(), pacote()])
      .then(([b, { createClient }]) => {
        if (typeof b?.supabaseUrl !== 'string' || typeof b?.supabaseKey !== 'string') throw new Error('sem_cliente');
        return createClient(b.supabaseUrl, b.supabaseKey, {
          auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false, storageKey: 'fk-crm-vigia' },
          realtime: { worker: !!comWorker, heartbeatCallback: batimento },
        });
      })
      .catch((e) => { clientePromessa = null; throw e; });
  }
  return clientePromessa;
}
/** Só para os testes. */
export function _zerarClienteVigia() { clientePromessa = null; aoBatimento = null; }
// para o crmTempoReal a aba da líder está sempre "visível": o canal fica aberto com o Avec na frente
const SEMPRE_VISIVEL = Object.freeze({ visibilityState: 'visible', addEventListener() {}, removeEventListener() {} });

const temFoco = () => { try { return typeof document !== 'undefined' && document.hasFocus() === true; } catch { return false; } };

// sem data.em no aviso (o "Testar aviso", servidor antigo): vale a hora em que ele apareceu, com esta folga de relógio
const FOLGA_TELA_MS = 2 * 60_000;
/**
 * O aviso do sistema desta fala está na tela? true | false; null = não dá para saber. em = a hora da fala
 * (ultimaDoClienteEm): só conta o aviso com data.em >= em (revisão 2, 30/10/2026: o aviso de uma fala antiga da
 * mesma conversa, esquecido na Central de Notificações, não cala a fala nova). Sem em: qualquer aviso da tag.
 */
export async function pushNaTela(tag, emOuOpcoes = null, opcoes = {}) {
  const em = typeof emOuOpcoes === 'string' || typeof emOuOpcoes === 'number' ? emOuOpcoes : null;
  const { sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : null } = (emOuOpcoes && typeof emOuOpcoes === 'object' ? emOuOpcoes : opcoes) || {};
  try {
    const reg = sw && typeof sw.getRegistration === 'function' ? await sw.getRegistration() : null;
    if (!reg || typeof reg.getNotifications !== 'function') return null;
    const lista = await reg.getNotifications({ tag });
    if (!Array.isArray(lista)) return null;
    const daFala = em != null ? Date.parse(em) : Number.NaN;
    if (!Number.isFinite(daFala)) return lista.length > 0;
    return lista.some((n) => {
      const doAviso = Date.parse(n?.data?.em || '');
      if (Number.isFinite(doAviso)) return doAviso >= daFala;
      const quando = Number(n?.timestamp);
      return Number.isFinite(quando) && quando >= daFala - FOLGA_TELA_MS;
    });
  } catch { return null; }
}
const permissaoDoNavegador = () => {
  try { return typeof window !== 'undefined' && 'Notification' in window ? window.Notification.permission : 'sem_suporte'; } catch { return 'sem_suporte'; }
};

/** O aviso pequeno do canto quando o som deveria sair e o navegador ainda não deixou (depois de recarregar). */
export function AvisoSomTravado({ onLigar }) {
  return (
    <button
      type="button"
      onClick={onLigar}
      className="fixed right-4 bottom-20 md:bottom-4 z-[90] inline-flex items-center gap-2 rounded-pill border border-brand-200 bg-white px-3.5 py-2 text-[13px] font-medium text-ink shadow-card transition-colors hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
    >
      <Volume2 size={15} className="text-brand shrink-0" aria-hidden="true" />
      {TEXTO_LIGAR_SOM}
    </button>
  );
}

function Vigia({ user }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navRef = useRef(navigate);
  navRef.current = navigate;
  const userRef = useRef(user);
  userRef.current = user;
  const rotaRef = useRef(pathname);
  rotaRef.current = pathname;
  // o mesmo id das preferências do CRM (quemDaSessao no servidor: o perfil, senão o usuário)
  const euId = String(user?.professionalId || user?.id || '').slice(0, 80) || 'sessao';
  const euRef = useRef(euId);
  euRef.current = euId;
  const [total, setTotal] = useState(() => lerTotalDasAbas());
  const [lider, setLider] = useState(false);
  const [travado, setTravado] = useState(null);   // null = sem aviso; número = o volume do toque que não saiu

  // o som do CRM NESTA aba, com as preferências da pessoa; travado = o aviso do canto
  const tocarRef = useRef(null);
  tocarRef.current = async () => {
    const p = APARENCIA ? lerPrefs(chavePrefs(euRef.current)) : validarPrefs(null);
    if (!SOM || p.som === false) return 'mudo';
    const r = await tocarAviso({ volume: p.volume });
    if (r === 'travado') setTravado(p.volume);
    return r;
  };
  const tocarAqui = () => tocarRef.current();

  // a inscrição do computador no push, em silêncio (frente do push: crmAvisoComputador.js)
  useEffect(() => { try { Promise.resolve(reinscreverSeConcedido(userRef.current)).catch(() => {}); } catch { /* segue */ } }, []);
  // a Caixa desta aba sem foco deixa o aviso para o vigia (um som só; revisão 2)
  useEffect(() => marcarVigiaMontado(), []);
  // o som destrava em qualquer gesto no DNA (não só dentro do CRM); destravou, o aviso do canto some
  useEffect(() => (SOM && typeof document !== 'undefined' ? prenderDestravar(document) : undefined), []);
  useEffect(() => ouvirSom((estado) => { if (estado === 'running') setTravado(null); }), []);
  // a Caixa não conseguiu tocar (o navegador ainda não deixou): o mesmo aviso do canto
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const f = (e) => { const v = Number(e?.detail?.volume); setTravado(Number.isFinite(v) && v > 0 ? v : 0.25); };
    window.addEventListener(EVENTO_SOM_TRAVADO, f);
    return () => window.removeEventListener(EVENTO_SOM_TRAVADO, f);
  }, []);

  // as outras abas: o total que a líder escreveu
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const f = (e) => { if (e.key === CHAVE_VIGIA_TOTAL) setTotal(lerTotalDasAbas()); };
    window.addEventListener('storage', f);
    return () => window.removeEventListener('storage', f);
  }, []);

  // a aba em foco que não é a líder: a líder pediu o som (CHAVE_VIGIA_TOCAR); toca e responde em CHAVE_VIGIA_TOCOU
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const f = (e) => { Promise.resolve(responderPedidoDeSom(e, { temFoco, tocar: tocarAqui, agoraMs: Date.now() })).catch(() => {}); };
    window.addEventListener('storage', f);
    return () => window.removeEventListener('storage', f);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // o "(N) " no título fora do CRM (lá o CrmTrabalho põe "(N) CRM da Sofia"); outra parte trocou o título: reaplica
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const aplicar = () => {
      if (naRotaDoCrm(rotaRef.current)) return;
      const quer = tituloComContagem(document.title, total);
      if (document.title !== quer) document.title = quer;
    };
    aplicar();
    const el = document.querySelector?.('title');
    const obs = el && typeof MutationObserver !== 'undefined' ? new MutationObserver(aplicar) : null;
    obs?.observe(el, { childList: true, characterData: true, subtree: true });
    return () => {
      obs?.disconnect();
      if (!naRotaDoCrm(rotaRef.current)) { const limpo = tirarPrefixo(document.title); if (document.title !== limpo) document.title = limpo; }
    };
  }, [total, pathname]);

  // o service worker (todas as abas): o clique no push (o roteador abre, sem recarregar, e responde), a chegada do
  // push (grava, marca e, com pedirSom, toca se esta aba tem foco) e a troca da inscrição (refaz a do computador)
  useEffect(() => {
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : null;
    if (!sw || typeof sw.addEventListener !== 'function') return undefined;
    const leitor = criarLeitorDoServiceWorker({
      navegar: (url) => navRef.current(url),
      origem: typeof window !== 'undefined' ? window.location?.origin : null,
      focar: () => { try { window.focus(); } catch { /* segue */ } },
      temFoco,
      tocar: tocarAqui,
      reinscrever: () => reinscreverSeConcedido(userRef.current, { forcar: true }),
    });
    const f = (e) => { Promise.resolve(leitor(e)).catch(() => {}); };
    sw.addEventListener('message', f);
    return () => sw.removeEventListener('message', f);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // a aba líder deste navegador
  useEffect(() => {
    const l = criarLideranca({ locks: typeof navigator !== 'undefined' ? navigator.locks || null : null, janela: typeof window !== 'undefined' ? window : null, aoMudar: setLider });
    l.iniciar();
    return () => l.parar();
  }, []);

  // só a líder lê e avisa (a memória da leitura anterior e as recusas seguidas valem enquanto esta tela vive)
  const leituraRef = useRef({ antes: null, carimbo: null, total: null });
  const recusasRef = useRef(0);
  useEffect(() => {
    if (!lider) return undefined;
    let vivo = true;
    let suspeito = false;
    let canal = null;

    function mostrarAviso(c) {
      try {
        const n = new window.Notification(TITULO_AVISO_MENSAGEM, {
          body: corpoAvisoMensagem(c.unidade ? (NOME_CURTO[c.unidade] || null) : null),
          tag: tagAvisoMensagem(c.id), renotify: true, requireInteraction: true, silent: false,
        });
        n.onclick = () => {
          try { window.focus(); } catch { /* segue */ }
          try { navRef.current(urlAvisoMensagem(c.id)); } catch { /* segue */ }
          try { n.close(); } catch { /* segue */ }
        };
        return true;
      } catch { return false; }
    }

    // um som só por leva: a regra inteira em crmVigia.criarAvisador; aqui só as peças da tela
    const avisador = criarAvisador({
      tocar: tocarAqui,
      pedirSom: () => pedirSomNaAbaComFoco({ janela: typeof window !== 'undefined' ? window : null }),
      mostrarAviso,
      pushNaTela: (tag, em) => pushNaTela(tag, em),
      pushAtivo: () => avisosComputadorAtivos(userRef.current) === true,
      caixaAtiva: () => caixaAtivaAgora({ agoraMs: Date.now() }),
      temFoco,
      permissao: permissaoDoNavegador,
      // a chave "Aviso do navegador" da Aparência desligada: nada de aviso da página (revisão 2)
      notificar: () => (APARENCIA ? lerPrefs(chavePrefs(euRef.current)) : validarPrefs(null)).notificar === true,
    });

    // o canal só com o cliente pronto; sem ele (o pacote não abriu), fica a reserva por consulta
    let ligando = false;
    function ligarCanal() {
      if (canal || ligando) return;
      ligando = true;
      clienteDoVigia().then(() => {
        if (!vivo || canal) return;
        canal = criarTempoReal({
          criarCliente: clienteDoVigia, doc: SEMPRE_VISIVEL,
          aoSinal: (info) => { agenda.sinal(info?.t); },
          aoEstado: () => { agenda.reagendar(); },
        });
        canal.iniciar();
      }).catch(() => { ligando = false; });
    }

    async function ler({ apos = null } = {}) {
      let api;
      try { api = await import('../../utils/crmSofiaApi.js'); } catch { return null; }
      // a prévia local (?demo=1, só em DEV) tem dados inventados: o vigia fica quieto
      if (import.meta.env.DEV && api.demoLigado()) return { parar: true };
      const anterior = leituraRef.current;
      let r;
      try { r = await api.crmApi.avisos({ carimbo: anterior.carimbo || undefined, apos: apos || undefined }); } catch (e) {
        // recusa passageira não desliga: só 404 ou sem token param de vez; 401/403 esperam 5 min, 3 seguidas param
        const d = depoisDaFalha(e, recusasRef.current);
        recusasRef.current = d.seguidas;
        if (d.parar) return { parar: true };
        if (d.esperaMs) return { esperaMs: d.esperaMs };
        return null;   // REGRA LINEAR: o título fica com o que tinha; a reserva tenta de novo
      }
      recusasRef.current = 0;
      if (!vivo || !r || r.ok !== true) return null;
      const agoraMs = Date.now();
      const p = processarLeitura({ resposta: r, antes: anterior.antes, totalAntes: anterior.total, agoraMs });
      leituraRef.current = { antes: p.antes, carimbo: p.carimbo, total: p.total };
      if (p.total != null) setTotal(p.total);
      ligarCanal();   // o canal só depois da primeira leitura boa (sem acesso, nem conecta)
      if (p.novas.length) await avisador.avisar(p.novas);
      return null;
    }

    const agenda = criarAgenda({ ler, aoVivo: () => !!canal?.aoVivo() && !suspeito, caixaAtiva: () => caixaAtivaAgora({ agoraMs: Date.now() }) });
    const batimento = (status) => {
      if (status === 'timeout' || status === 'disconnected' || status === 'error') {
        if (!suspeito) { suspeito = true; agenda.reagendar(); }
      } else if (status === 'ok' && suspeito) { suspeito = false; agenda.reagendar(); }
    };
    aoBatimento = batimento;
    agenda.iniciar();
    return () => {
      vivo = false;
      avisador.parar();
      agenda.parar();
      canal?.parar();
      if (aoBatimento === batimento) aoBatimento = null;
    };
  }, [lider]); // eslint-disable-line react-hooks/exhaustive-deps

  if (travado == null) return null;
  return (
    <AvisoSomTravado onLigar={() => { testarSom(travado); setTravado(null); }} />
  );
}

// a marca da sessão para a key do Vigia: um resumo curto do token (o token em si não fica na árvore do React)
export function marcaDaSessao(token) {
  const s = String(token || '');
  if (!s) return '0';
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
  return h.toString(36);
}
const tokenDaSessao = () => { try { return marcaDaSessao(sessionToken()); } catch { return '0'; } };

/** O portão: o mesmo do item "CRM da Sofia" do Sidebar (comCrm), o interruptor CRM_VIGIA e o computador. */
export default function VigiaCrm() {
  const { user, isMaster } = useAuth() || {};
  const dataCtx = useData();
  const professionals = dataCtx?.professionals || [];
  const papel = user?.professionalId ? professionals.find(p => p.id === user.professionalId)?.role || null : null;
  const usaFila = usaFilaNoMenu({ user, papel, iaMenu: IA_ATENDENTE_MENU });
  const computador = eComputador(typeof navigator !== 'undefined' ? navigator.userAgent : '');
  const atd = useAtendimentoAcesso({ enabled: CRM_VIGIA && computador && usaFila });
  if (!user || !vigiaLigadoPara({ vigia: CRM_VIGIA, crmMenu: CRM_SOFIA_MENU, iaMenu: IA_ATENDENTE_MENU, usaFila, podeFila: atd.podeFila, isMaster, computador })) return null;
  // a sessão mudou (outra pessoa no balcão, ou entrou de novo): o vigia recomeça do zero, inclusive as recusas
  return <Vigia key={`${user.id || ''}|${user.professionalId || ''}|${tokenDaSessao()}`} user={user} />;
}
